import { useEffect, useRef, useState } from "react";
import { ReminderToast, isBadMomentForReminder } from "@/components/ui/ReminderToast";
import { useDashboardSummary } from "@/api/dashboard";
import { useAuth } from "@/context/AuthContext";

// O app só ajuda quem anota. Frases que servem pra qualquer momento (não dizem se a pessoa já
// anotou ou não hoje), usadas também quando o resumo ainda não carregou.
const DISCIPLINE_MESSAGES = [
  "Não adianta ter o app se você não anota. Só anotando todos os dias é que vem o resultado.",
  "Já anotou seus gastos de hoje? Um minuto agora evita a surpresa no fim do mês.",
  "Controle financeiro não é o app, é o hábito. Quem anota todo dia é quem vê a vida mudar.",
  "Gasto que não é anotado vira dinheiro que some sem explicação. Não deixe nenhum de fora.",
  "Deixar pra anotar depois é o jeito mais fácil de esquecer. Que tal anotar agora?",
  "Aquele cafezinho e aquele Pix rápido também contam. Anotou tudo hoje?",
  "O app só mostra a verdade se você contar tudo pra ele. Anote cada gasto, todo dia.",
  "A disciplina de anotar todo dia é o que separa quem quer economizar de quem economiza.",
  "Ninguém muda de vida só baixando um app. Muda anotando, um dia de cada vez.",
  "Cada dia anotado é um passo pra fechar o mês no azul. Não pule o de hoje.",
  "Pequenos gastos esquecidos fazem o maior estrago. Registre todos, todos os dias.",
  "Anotar todo dia transforma seus números em decisões melhores — e numa vida mais tranquila.",
];

const NOT_LOGGED_TODAY_MESSAGES = [
  "Hoje ainda não tem nenhuma anotação. Lembre: o app só faz diferença se você anotar todos os dias.",
  "Você ainda não anotou nada hoje. Não adianta ter o app sem a disciplina de anotar — leva um minuto.",
  "Nenhum gasto anotado hoje ainda. Só anotando todo dia você vai ver resultado de verdade.",
];

/**
 * Quem ainda não anotou nada hoje ouve só sobre isso (é o esquecimento que o lembrete quer pegar);
 * quem já anotou ouve sobre manter o ritmo, misturado com as frases gerais. A sequência só é
 * citada a partir de 2 dias ("1 dia seguido" soa estranho).
 */
function messagePool(streak: { days: number; loggedToday: boolean } | undefined): string[] {
  if (!streak) return DISCIPLINE_MESSAGES;
  const { days, loggedToday } = streak;
  if (!loggedToday && days >= 2) {
    return [
      `Você vinha anotando há ${days} dias seguidos e hoje ainda não tem nada. Não deixe essa sequência morrer.`,
      `Sequência de ${days} dias em risco: hoje ainda não tem nenhuma anotação. É a constância que traz resultado.`,
    ];
  }
  if (!loggedToday && days === 1) {
    return [
      "Ontem você anotou, hoje ainda não. É anotando todo dia, sem pular, que o resultado aparece.",
      ...NOT_LOGGED_TODAY_MESSAGES,
    ];
  }
  if (!loggedToday) return NOT_LOGGED_TODAY_MESSAGES;
  return [
    ...DISCIPLINE_MESSAGES,
    "Você já anotou hoje. Só confira se não ficou nenhum gasto de fora: o resultado vem de anotar tudo.",
    ...(days >= 2 ? [`${days} dias seguidos anotando — é essa disciplina que muda sua vida financeira. Anotou tudo de hoje?`] : []),
  ];
}

const LAST_MESSAGE_KEY = "controlenext-log-reminder-last";

// Sorteia uma frase, sem repetir a última que a pessoa viu (mesmo de uma entrada pra outra).
function pickMessage(pool: string[]) {
  let last: string | null = null;
  try {
    last = localStorage.getItem(LAST_MESSAGE_KEY);
  } catch {
    // sem localStorage: só não evita a repetição
  }
  const options = pool.length > 1 ? pool.filter((m) => m !== last) : pool;
  const message = options[Math.floor(Math.random() * options.length)];
  try {
    localStorage.setItem(LAST_MESSAGE_KEY, message);
  } catch {
    // idem
  }
  return message;
}

// Uma "entrada" é abrir o app (aba nova, app instalado aberto do zero, login) ou voltar pra ele
// depois de um tempo fora. Recarregar a página ou trocar de aba rapidinho não conta.
const ENTRY_DELAY_MS = 4_000;
const RETRY_MS = 5_000;
const AWAY_MS = 30 * 60_000;
// Em servidor acordando devagar, espera um pouco pelo resumo pra frase poder falar do dia de hoje.
const MAX_SUMMARY_WAIT_MS = 15_000;
const ENTRY_KEY = "controlenext-log-reminder";

interface EntryState {
  shownAt: number;
  hiddenAt: number;
}

// Guardado por aba (sessionStorage), como uma sessão de visita.
function readEntry(): EntryState {
  try {
    const raw = sessionStorage.getItem(ENTRY_KEY);
    if (raw) return JSON.parse(raw) as EntryState;
  } catch {
    // sem sessionStorage: cada carregamento conta como uma entrada
  }
  return { shownAt: 0, hiddenAt: 0 };
}

function writeEntry(state: EntryState) {
  try {
    sessionStorage.setItem(ENTRY_KEY, JSON.stringify(state));
  } catch {
    // idem
  }
}

/**
 * Pra quem assina: toda vez que a pessoa entra no app, uma notificação lembrando de anotar os
 * gastos do dia — o app só dá resultado com a disciplina de anotar todo dia.
 */
export function DailyLogReminder({ onQuickAdd }: { onQuickAdd: () => void }) {
  const { user } = useAuth();
  const eligible = !!user?.isPremium;
  const { data: summary, isPending } = useDashboardSummary();
  const [message, setMessage] = useState<string | null>(null);
  const summaryRef = useRef({ streak: summary?.streak, isPending });
  summaryRef.current = { streak: summary?.streak, isPending };

  useEffect(() => {
    if (!eligible) return;
    let timer: number | undefined;
    let scheduledAt = 0;

    const attempt = () => {
      const waitingSummary = summaryRef.current.isPending && Date.now() - scheduledAt < MAX_SUMMARY_WAIT_MS;
      if (waitingSummary || isBadMomentForReminder()) {
        timer = window.setTimeout(attempt, RETRY_MS);
        return;
      }
      setMessage(pickMessage(messagePool(summaryRef.current.streak)));
      writeEntry({ ...readEntry(), shownAt: Date.now() });
    };

    const scheduleIfNewEntry = () => {
      const { shownAt, hiddenAt } = readEntry();
      const cameBack = hiddenAt > shownAt && Date.now() - hiddenAt >= AWAY_MS;
      if (shownAt && !cameBack) return;
      window.clearTimeout(timer);
      scheduledAt = Date.now();
      timer = window.setTimeout(attempt, ENTRY_DELAY_MS);
    };

    const onVisibilityChange = () => {
      if (document.hidden) writeEntry({ ...readEntry(), hiddenAt: Date.now() });
      else scheduleIfNewEntry();
    };

    scheduleIfNewEntry();
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [eligible]);

  if (!eligible || !message) return null;

  return (
    <ReminderToast
      message={message}
      actionLabel="Anotar agora"
      onAction={() => {
        setMessage(null);
        onQuickAdd();
      }}
      onClose={() => setMessage(null)}
    />
  );
}
